import {useState} from 'react';
import {ChevronLeft, ChevronRight, Info, ListFilter, Search, X} from 'lucide-react';
import {Popover, PopoverContent, PopoverTrigger} from '@common-web/components/ui/popover';
import {Tooltip, TooltipContent, TooltipTrigger} from '@common-web/components/ui/tooltip';
import {cn} from '@common-web/lib/utils';
import {CheckRow} from '@internal/pages/group-pipeline/components/CheckRow';

export interface FilterSection {
  key: string;
  label: string;
  options: {value: string; label: string}[];
  selected: string[];
  // Options that can't apply in the current view; shown muted with the reason.
  disabled?: {values: string[]; reason: string};
}

export function PipelineFilters({
  sections,
  onChange,
  onClearAll,
}: {
  sections: FilterSection[];
  onChange: (key: string, selected: string[]) => void;
  onClearAll: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const active = sections.find((s) => s.key === activeKey) ?? null;
  const applied = sections.filter((s) => s.selected.length > 0);
  const needle = query.trim().toLowerCase();

  const openSection = (key: string | null) => {
    setActiveKey(key);
    setQuery('');
  };

  const toggle = (section: FilterSection, value: string) =>
    onChange(
      section.key,
      section.selected.includes(value)
        ? section.selected.filter((v) => v !== value)
        : [...section.selected, value],
    );

  const labelOf = (section: FilterSection, value: string) =>
    section.options.find((o) => o.value === value)?.label ?? value;
  // First selected value in menu order, then "+N" for the rest.
  const summary = (section: FilterSection) => {
    const ordered = section.options.map((o) => o.value).filter((v) => section.selected.includes(v));
    const [first, ...rest] = ordered.length ? ordered : section.selected;
    return rest.length ? `${labelOf(section, first)} +${rest.length}` : labelOf(section, first);
  };
  const isDisabled = (section: FilterSection, value: string) =>
    section.disabled?.values.includes(value) ?? false;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) openSection(null);
      }}
    >
      {/* `contents` lets each chip and button wrap on its own in the toolbar row. */}
      <div className='contents'>
        {applied.map((section) => {
          const disabledSelected = section.selected.filter((v) => isDisabled(section, v));
          const allDisabled = disabledSelected.length === section.selected.length;
          // Menu order, so the first value shown is the earliest option.
          const ordered = section.options
            .map((o) => o.value)
            .filter((v) => section.selected.includes(v));
          const [first, ...rest] = ordered.length ? ordered : section.selected;

          const chip = (
            <button
              type='button'
              onClick={() => {
                openSection(section.key);
                setOpen(true);
              }}
              className={cn(
                'inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-lg border bg-white pl-3 text-sm font-semibold',
                // Room for the clear button, plus the info icon when it shows.
                disabledSelected.length > 0 && section.disabled ? 'pr-14' : 'pr-9',
                allDisabled ? 'border-gray-300 text-gray-400' : 'border-gray-900 text-gray-900',
              )}
            >
              {section.label}:
              <span
                className={cn(
                  'max-w-[160px] truncate font-medium',
                  isDisabled(section, first) ? 'text-gray-400' : 'text-gray-700',
                )}
              >
                {labelOf(section, first)}
              </span>
              {rest.length > 0 && <span className='font-medium text-gray-500'>+{rest.length}</span>}
            </button>
          );

          return (
            <div key={section.key} className='relative'>
              {rest.length > 0 ? (
                <Tooltip>
                  <TooltipTrigger asChild>{chip}</TooltipTrigger>
                  <TooltipContent className='max-w-xs bg-gray-900 text-white'>
                    {ordered.map((v) => labelOf(section, v)).join(', ')}
                  </TooltipContent>
                </Tooltip>
              ) : (
                chip
              )}
              {disabledSelected.length > 0 && section.disabled && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={0}
                      aria-label={section.disabled.reason}
                      className='absolute right-8 top-1/2 inline-flex -translate-y-1/2 cursor-help text-gray-400 hover:text-gray-700'
                    >
                      <Info className='h-3.5 w-3.5' />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className='max-w-xs bg-gray-900 text-white'>
                    {section.disabled.reason}
                  </TooltipContent>
                </Tooltip>
              )}
              <button
                type='button'
                aria-label={`Clear ${section.label}`}
                onClick={() => onChange(section.key, [])}
                className='absolute right-2 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900'
              >
                <X className='h-3.5 w-3.5' />
              </button>
            </div>
          );
        })}

        <PopoverTrigger asChild>
          <button
            type='button'
            className={cn(
              'inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-lg border bg-white px-3 text-sm font-semibold text-gray-700 hover:border-gray-500',
              open
                ? 'border-gray-900'
                : applied.length > 0
                  ? 'border-dashed border-gray-300'
                  : 'border-gray-200',
            )}
          >
            <ListFilter className='h-4 w-4' />
            Filter
          </button>
        </PopoverTrigger>

        {applied.length > 0 && (
          <button
            type='button'
            onClick={onClearAll}
            className='px-1 text-sm font-semibold text-gray-500 hover:text-gray-900'
          >
            Clear all
          </button>
        )}
      </div>

      <PopoverContent
        align='start'
        className='w-[300px] overflow-hidden rounded-xl border-gray-200 p-0 shadow-[0_16px_40px_rgba(15,23,42,0.16)]'
      >
        {active ? (
          <>
            <div className='flex items-center gap-1.5 px-3 pb-2 pt-3'>
              <button
                type='button'
                aria-label='Back to all filters'
                onClick={() => openSection(null)}
                className='-ml-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100'
              >
                <ChevronLeft className='h-4 w-4' />
              </button>
              <span className='text-sm font-bold text-gray-900'>{active.label}</span>
            </div>
            <div className='max-h-72 overflow-y-auto px-1.5 pb-1.5'>
              {active.options.map((option) => (
                <CheckRow
                  key={option.value}
                  label={option.label}
                  checked={active.selected.includes(option.value)}
                  disabled={isDisabled(active, option.value)}
                  onToggle={() => toggle(active, option.value)}
                />
              ))}
              {active.disabled && (
                <p className='flex items-start gap-1.5 px-2 pb-1 pt-2 text-xs text-gray-500'>
                  <Info className='mt-px h-3.5 w-3.5 shrink-0' />
                  {active.disabled.reason}
                </p>
              )}
            </div>
          </>
        ) : (
          <>
            <div className='px-3 pb-2 pt-3'>
              <label className='relative block'>
                <Search className='absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-500' />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder='Search filters'
                  aria-label='Find a filter'
                  className='h-[34px] w-full rounded-lg border border-gray-200 bg-gray-50 pl-8 pr-2 text-sm outline-none focus:bg-white focus:ring-2 focus:ring-ring'
                />
              </label>
            </div>
            <div className='max-h-80 overflow-y-auto px-1.5 pb-1.5'>
              {sections
                .filter((s) => !needle || s.label.toLowerCase().includes(needle))
                .map((section) => (
                  <button
                    key={section.key}
                    type='button'
                    onClick={() => openSection(section.key)}
                    className='flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm hover:bg-gray-100'
                  >
                    <span className='flex-1 text-gray-900'>{section.label}</span>
                    {section.selected.length > 0 && (
                      <span className='max-w-[140px] truncate text-gray-500'>
                        {summary(section)}
                      </span>
                    )}
                    <ChevronRight className='h-4 w-4 shrink-0 text-gray-500' />
                  </button>
                ))}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
